import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import {
  getConsentPreferences,
  saveConsentPreferences,
  hasConsentChoice,
  isAnalyticsEnabled,
} from '@/lib/cookie-consent';
import { usePlatformSettingsContext } from '@/contexts/PlatformSettingsContext';

interface CookieConsentContextType {
  hasConsented: boolean;
  analyticsEnabled: boolean;
  showBanner: boolean;
  showPreferences: boolean;
  /**
   * True once the consent gate has settled: bypass flag enabled, the user
   * has a stored consent choice (accepted OR rejected), OR the
   * platform-settings config has finished loading. Consumers that need to
   * defer side-effects (e.g. GA bootstrap) until consent state is known
   * should gate on `consentReady` rather than reaching into
   * PlatformSettingsContext.
   */
  consentReady: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  updatePreferences: (analytics: boolean) => void;
  openPreferences: () => void;
  closePreferences: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | null>(null);

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  // Check platform settings for bypass flag AND loading states
  const { featureFlags, isLoading, isPublicFlagsLoading } = usePlatformSettingsContext();
  const bypassConsent = featureFlags.bypass_cookie_consent_banner;
  
  // Combine loading states to prevent banner flash during startup
  // - isPublicFlagsLoading: covers auth unknown + unauthenticated users with pending public flags
  // - isLoading: covers authenticated users with pending full settings query
  const consentConfigLoading = isLoading || isPublicFlagsLoading;

  const [hasConsented, setHasConsented] = useState(() => hasConsentChoice());
  const [analyticsEnabled, setAnalyticsEnabled] = useState(() => isAnalyticsEnabled());
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    const preferences = getConsentPreferences();
    if (preferences) {
      setHasConsented(true);
      setAnalyticsEnabled(preferences.analytics);
    }
  }, []);

  // Auto-enable analytics when bypass is active
  useEffect(() => {
    if (bypassConsent) {
      setAnalyticsEnabled(true);
      setHasConsented(true);
    }
  }, [bypassConsent]);

  const acceptAll = useCallback(() => {
    saveConsentPreferences(true);
    setHasConsented(true);
    setAnalyticsEnabled(true);
    setShowPreferences(false);
  }, []);

  const rejectAll = useCallback(() => {
    saveConsentPreferences(false);
    setHasConsented(true);
    setAnalyticsEnabled(false);
    setShowPreferences(false);
  }, []);

  const updatePreferences = useCallback((analytics: boolean) => {
    saveConsentPreferences(analytics);
    setHasConsented(true);
    setAnalyticsEnabled(analytics);
    setShowPreferences(false);
  }, []);

  const openPreferences = useCallback(() => setShowPreferences(true), []);
  const closePreferences = useCallback(() => setShowPreferences(false), []);

  // Phase 1 — `consentReady` exposes whether the consent gate has settled
  // (bypass on, stored choice exists, or settings finished loading).
  const consentReady = bypassConsent || hasConsented || !consentConfigLoading;

  return (
    <CookieConsentContext.Provider value={{
      hasConsented: bypassConsent || hasConsented,
      analyticsEnabled: bypassConsent || analyticsEnabled,
      // Don't show banner while loading settings (prevents flash when bypass is enabled)
      showBanner: !bypassConsent && !hasConsented && !consentConfigLoading,
      showPreferences,
      consentReady,
      acceptAll,
      rejectAll,
      updatePreferences,
      openPreferences,
      closePreferences,
    }}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider');
  }
  return context;
}
